import useOnMount from '@/hooks/useOnMount';
import { Box, Flex, IconCheck, Text } from '@cmpsr/components';
import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import { useEffect, useRef, useState } from 'react';

const fadeInOut = keyframes`
  0% {
    opacity: 0.1;
  }
  5% {
    opacity: 1;
  }
  95% {
    opacity: 1;
  }
  100% {
    opacity: 0.1;
  }
`;

const MessageContainer = styled(Box)`
  position: relative;
  min-height: 80px;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;
  overflow-y: auto;
  scroll-behavior: smooth;
  padding: 40vh 0;
  margin: -40vh 0;
  pointer-events: none;
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari and Opera */
  }
`;

const Message = styled(Flex)<{ isActive: boolean; messageIntervalSeconds: number }>`
  animation: ${fadeInOut} ${(props) => props.messageIntervalSeconds}s ease-in-out;
  opacity: ${(props) => (props.isActive ? 1 : 0.1)};
  transition: opacity 0.3s ease-in-out;
  scroll-margin: 40vh;
  pointer-events: none;
  visibility: ${(props) => (props.isActive ? 'visible' : 'visible')};
  animation-play-state: ${(props) => (props.isActive ? 'running' : 'paused')};
  align-items: center;
  gap: 18px;
  padding: 15px 0;
`;

const IconCircle = styled(Flex)`
  width: 25px;
  height: 25px;
  border-radius: 50%;
  border: 1px solid #7cf1c7;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
`;

const MessageText = styled(Text)`
  font-size: 16px;
  font-weight: 500;
  line-height: 1.3em;
  color: white;
  flex: 1;
`;

export const VerticalCopyMarquee = ({
  messages,
  messageIntervalSeconds,
}: {
  messages: string[];
  messageIntervalSeconds: number;
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const messageRefs = useRef<(HTMLDivElement | null)[]>([]);
  useOnMount(() => {
    const interval = setInterval(() => {
      setActiveIndex((prev) => {
        return prev < messages.length - 1 ? prev + 1 : prev;
      });
    }, messageIntervalSeconds * 1000);

    return () => clearInterval(interval);
  });

  useEffect(() => {
    const activeRef = messageRefs.current[activeIndex];
    if (activeRef) {
      activeRef.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [activeIndex]);

  return (
    <MessageContainer>
      {messages.map((message, index) => (
        <Message
          key={index}
          ref={(el) => (messageRefs.current[index] = el)}
          isActive={index === activeIndex}
          messageIntervalSeconds={messageIntervalSeconds}
        >
          <IconCircle>
            <IconCheck color="#7CF1C7" size="s" />
          </IconCircle>
          <MessageText>{message}</MessageText>
        </Message>
      ))}
    </MessageContainer>
  );
};
